// Shared utility for accessing cached trade dates data
// Used by App, Portfolio, and other components to avoid duplicate API calls

import { ensureLatestByMarket, serializeLatestByMarket, isLatestDateListAtToday, isPerMarketLatestStale } from './marketTradeDates';
import { shouldUseFreshClientCache } from './clientCacheReload';

const TRADE_DATES_CACHE_KEY = 'tradeDatesCache';
const LATEST_TRADE_DATE_CACHE_KEY = 'latestTradeDateCache';
const LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY = 'latestTradeDateByMarketCache';
const CACHE_TTL = 60 * 60 * 1000; // 60 minutes in milliseconds

// Track in-flight requests to coordinate multiple components
const inFlightRequest = { promise: null, timestamp: null };
const inFlightLatestByMarket = { promise: null, timestamp: null };

const hasAnyLatestMarketDate = (data) => Boolean(data && (data.US || data.CA));

export const clearTradeDatesClientCache = () => {
  try {
    localStorage.removeItem(TRADE_DATES_CACHE_KEY);
    localStorage.removeItem(LATEST_TRADE_DATE_CACHE_KEY);
    localStorage.removeItem(LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY);
    inFlightRequest.promise = null;
    inFlightRequest.timestamp = null;
    inFlightLatestByMarket.promise = null;
    inFlightLatestByMarket.timestamp = null;
    console.log('[TRADE_DATES CACHE] Cleared trade dates client cache');
  } catch (error) {
    console.warn('[TRADE_DATES CACHE] Error clearing cache:', error);
  }
};

const isNonEmptyTradeDates = (data) => Array.isArray(data) && data.length > 0;

const saveLatestByMarketCache = (latestByMarket) => {
  if (!hasAnyLatestMarketDate(latestByMarket)) return;
  const cacheData = {
    data: latestByMarket,
    timestamp: Date.now()
  };
  localStorage.setItem(LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY, JSON.stringify(cacheData));
  // Legacy single-key cache (US default for older readers)
  if (latestByMarket.US) {
    localStorage.setItem(LATEST_TRADE_DATE_CACHE_KEY, JSON.stringify({
      data: latestByMarket.US,
      timestamp: Date.now()
    }));
  }
  console.log(`[TRADE_DATES CACHE] Saved latest by market: US=${latestByMarket.US}, CA=${latestByMarket.CA}`);
};

const fetchLatestTradeDatesByMarketFromServer = async (tradeDatesFallback = []) => {
  try {
    const response = await fetch('/api/latest_trade_dates');
    if (!response.ok) {
      throw new Error(`Failed to fetch latest trade dates by market: ${response.statusText}`);
    }
    const data = await response.json();
    return ensureLatestByMarket(data, tradeDatesFallback);
  } catch (error) {
    console.warn('[TRADE_DATES CACHE] latest_trade_dates API failed, using trade_dates fallback:', error);
    return ensureLatestByMarket({}, tradeDatesFallback);
  }
};

/**
 * Get cached trade dates, using cache if available and valid
 * Coordinates requests so if a request was sent recently, all components wait for the same request
 * @param {boolean} useCache - Whether to check cache first (default: true)
 * @returns {Promise<Array>} Trade dates array (strings in YYYY-MM-DD format)
 */
export const getCachedTradeDates = async (useCache = true) => {
  if (useCache && shouldUseFreshClientCache()) {
    useCache = false;
  }

  const now = Date.now();
  
  // STEP 1: Check for in-flight requests FIRST to coordinate simultaneous requests
  if (inFlightRequest.promise && inFlightRequest.timestamp) {
    const requestAge = now - inFlightRequest.timestamp;
    const REQUEST_COORDINATION_WINDOW = 20 * 1000; // 20 seconds
    
    if (requestAge < REQUEST_COORDINATION_WINDOW) {
      console.log(`[TRADE_DATES CACHE] Waiting for in-flight request (age: ${Math.round(requestAge / 1000)}s)`);
      try {
        const data = await inFlightRequest.promise;
        console.log(`[TRADE_DATES CACHE] Received data from coordinated request (${data?.length || 0} items)`);
        return data || [];
      } catch (error) {
        console.warn(`[TRADE_DATES CACHE] In-flight request failed, will retry:`, error);
        inFlightRequest.promise = null;
        inFlightRequest.timestamp = null;
      }
    } else {
      console.log(`[TRADE_DATES CACHE] In-flight request expired (age: ${Math.round(requestAge / 1000)}s), will make new request`);
      inFlightRequest.promise = null;
      inFlightRequest.timestamp = null;
    }
  }
  
  // STEP 2: Check cache if useCache is true
  if (useCache) {
    try {
      const cachedData = localStorage.getItem(TRADE_DATES_CACHE_KEY);
      if (cachedData) {
        const parsedCache = JSON.parse(cachedData);
        if (!parsedCache || !Array.isArray(parsedCache.data) || typeof parsedCache.timestamp !== 'number') {
          console.warn(`[TRADE_DATES CACHE] Invalid cache shape, removing`);
          localStorage.removeItem(TRADE_DATES_CACHE_KEY);
        } else {
        const list = parsedCache.data || [];
        if (isLatestDateListAtToday(list)) {
          console.log(`[TRADE_DATES CACHE] Frozen — latest trade_date is current NY date (${list.length} items)`);
          return list;
        }
        const cacheAge = now - parsedCache.timestamp;
        
        // Use cached data if it's less than 30 minutes old
        if (cacheAge < CACHE_TTL) {
          console.log(`[TRADE_DATES CACHE] Using cached data (age: ${Math.round(cacheAge / 1000)}s, ${list.length} items)`);
          return list;
        } else {
          console.log(`[TRADE_DATES CACHE] Cache expired (age: ${Math.round(cacheAge / 1000)}s), will fetch fresh data`);
        }
        }
      } else {
        console.log(`[TRADE_DATES CACHE] No cache found, will fetch fresh data`);
      }
    } catch (cacheError) {
      console.warn(`[TRADE_DATES CACHE] Error reading cache:`, cacheError);
      // Continue to fetch if cache read fails
    }
  }
  
  // STEP 3: Create a new request promise (cache expired or no cache)
  console.log(`[TRADE_DATES CACHE] Starting new API request`);
  
  const fetchPromise = (async () => {
    try {
      const response = await fetch('/api/trade_dates');
      if (!response.ok) {
        throw new Error(`Failed to fetch trade dates: ${response.statusText}`);
      }
      const data = await response.json();
      
      console.log(`[TRADE_DATES CACHE] Received ${data?.length || 0} items from server`);
      
      // Cache only non-empty data, so an empty API response does not overwrite a valid cache
      try {
        if (isNonEmptyTradeDates(data)) {
          const cacheData = {
            data: data,
            timestamp: Date.now()
          };
          localStorage.setItem(TRADE_DATES_CACHE_KEY, JSON.stringify(cacheData));
          console.log(`[TRADE_DATES CACHE] Saved to cache (${data?.length || 0} items)`);

          const latestByMarket = await fetchLatestTradeDatesByMarketFromServer(data);
          saveLatestByMarketCache(latestByMarket);
        } else {
          console.log(`[TRADE_DATES CACHE] API returned empty trade dates, skipping cache write`);
        }
      } catch (cacheError) {
        console.warn(`[TRADE_DATES CACHE] Error saving cache:`, cacheError);
        // Continue even if caching fails
      }
      
      return data || [];
    } catch (error) {
      console.error(`[TRADE_DATES CACHE] Error fetching trade dates:`, error);
      throw error; // Re-throw so waiting components know it failed
    } finally {
      // Remove from in-flight requests after completion
      inFlightRequest.promise = null;
      inFlightRequest.timestamp = null;
      console.log(`[TRADE_DATES CACHE] Request completed, removed from in-flight tracking`);
    }
  })();
  
  // Store the in-flight request with timestamp BEFORE making the request
  inFlightRequest.promise = fetchPromise;
  inFlightRequest.timestamp = now;
  
  try {
    const data = await fetchPromise;
    return data || [];
  } catch (error) {
    // If this request fails, return empty array
    return [];
  }
};

/**
 * Get cached latest trade dates per market (US, CA).
 * @param {boolean} useCache
 * @returns {Promise<{ US: string|null, CA: string|null }>}
 */
export const getCachedLatestTradeDatesByMarket = async (useCache = true) => {
  if (useCache && shouldUseFreshClientCache()) {
    useCache = false;
  }

  const now = Date.now();

  if (inFlightLatestByMarket.promise && inFlightLatestByMarket.timestamp) {
    const requestAge = now - inFlightLatestByMarket.timestamp;
    if (requestAge < 20 * 1000) {
      try {
        return await inFlightLatestByMarket.promise;
      } catch {
        inFlightLatestByMarket.promise = null;
        inFlightLatestByMarket.timestamp = null;
      }
    }
  }

  if (useCache) {
    try {
      const cachedData = localStorage.getItem(LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY);
      if (cachedData) {
        const parsedCache = JSON.parse(cachedData);
        if (parsedCache && typeof parsedCache.timestamp === 'number' && parsedCache.data) {
          let tradeDatesList = [];
          try {
            const tradeRaw = localStorage.getItem(TRADE_DATES_CACHE_KEY);
            if (tradeRaw) {
              const tradeParsed = JSON.parse(tradeRaw);
              tradeDatesList = tradeParsed?.data || [];
            }
          } catch { /* ignore */ }

          const normalized = ensureLatestByMarket(parsedCache.data);
          if (isPerMarketLatestStale(normalized, tradeDatesList)) {
            console.log('[LATEST_TRADE_DATE BY MARKET CACHE] Stale vs trade_dates head, refreshing');
            localStorage.removeItem(LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY);
          } else {
            const cacheAge = now - parsedCache.timestamp;
            if (cacheAge < CACHE_TTL && hasAnyLatestMarketDate(normalized)) {
              return normalized;
            }
            if (cacheAge < CACHE_TTL) {
              console.log('[LATEST_TRADE_DATE BY MARKET CACHE] Invalid cache (no US/CA date), refreshing');
            }
            localStorage.removeItem(LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY);
          }
        }
      }
    } catch (error) {
      console.warn('[LATEST_TRADE_DATE BY MARKET CACHE] Error reading cache:', error);
    }
  }

  const fetchPromise = (async () => {
    try {
      const tradeDates = await getCachedTradeDates(useCache);
      const latestByMarket = await fetchLatestTradeDatesByMarketFromServer(tradeDates);
      saveLatestByMarketCache(latestByMarket);
      return latestByMarket;
    } finally {
      inFlightLatestByMarket.promise = null;
      inFlightLatestByMarket.timestamp = null;
    }
  })();

  inFlightLatestByMarket.promise = fetchPromise;
  inFlightLatestByMarket.timestamp = now;

  try {
    return await fetchPromise;
  } catch (error) {
    console.error('[LATEST_TRADE_DATE BY MARKET CACHE] Error fetching:', error);
    const tradeDates = await getCachedTradeDates(false).catch(() => []);
    return ensureLatestByMarket({}, tradeDates);
  }
};

/**
 * Get latest trade date for a market (defaults to US).
 * @param {string} market - 'US' | 'CA'
 * @returns {Promise<string|null>}
 */
export const getCachedLatestTradeDate = async (market = 'US') => {
  const latestByMarket = await getCachedLatestTradeDatesByMarket();
  const key = (market || 'US').toUpperCase() === 'CA' ? 'CA' : 'US';
  return latestByMarket[key] || latestByMarket.US || latestByMarket.CA || null;
};

/** Synchronous read of cached per-market latest (for cache invalidation guards). */
export const getLatestTradeDatesByMarketSync = () => {
  try {
    const raw = localStorage.getItem(LATEST_TRADE_DATE_BY_MARKET_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.data) return parsed.data;
    }
  } catch { /* ignore */ }
  return null;
};

/** Stable key for portfolio/watchlist cache invalidation. */
export const getLatestTradeDateKeySync = () => {
  const byMarket = getLatestTradeDatesByMarketSync();
  if (byMarket) return serializeLatestByMarket(byMarket);
  try {
    const raw = localStorage.getItem(LATEST_TRADE_DATE_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.data) return `US:${parsed.data}|CA:${parsed.data}`;
    }
  } catch { /* ignore */ }
  return null;
};
